<template>
  <b-card header-tag="header">
    <template #header>
      <div>
        <h6 class="mb-0"><i class="fa fa-ellipsis-v mr-2 drag-handle"></i><i class="fa fa-trophy"></i> PvP Stats</h6>
      </div>
    </template>

    <b-tabs content-class="mt-3">
      <b-tab title="Ranked" active>
        <b-row class="mb-3">
          <b-col md="6">
            <b-input-group prepend="Wins">
              <b-form-input readonly :value="userData.pvp.ranked_wins"></b-form-input>
            </b-input-group>
          </b-col>
          <b-col md="6">
            <b-input-group prepend="Losses">
              <b-form-input readonly :value="userData.pvp.ranked_losses"></b-form-input>
            </b-input-group>
          </b-col>
        </b-row>

        <b-row class="mb-3">
          <b-col md="6">
            <b-input-group prepend="Current Win-Streak">
              <b-form-input readonly :value="userData.pvp.ranked_cur_streak"></b-form-input>
              <b-input-group-append is-text v-b-tooltip.hover title="All Time Highest Win-Streak">
                <i class="fa fa-line-chart mr-1"></i> {{ userData.pvp.ranked_max_streak }}
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col md="6">
            <b-input-group prepend="Wins Since Last PvP Chest">
              <b-form-input readonly :value="userData.pvp.ranked_chest_wins"></b-form-input>
            </b-input-group>
          </b-col>
        </b-row>

        <b-row class="mb-3">
          <b-col md="6">
            <b-input-group prepend="WOTD" v-b-tooltip.hover title="Win of the Day">
              <b-input-group-prepend is-text>
                <i :class="isWOTD(userData.pvp.ranked_wotd) ? 'fa fa-check text-success' : 'fa fa-times text-danger'"></i>
              </b-input-group-prepend>
              <b-form-input readonly :value="formatWOTD(userData.pvp.ranked_wotd)"></b-form-input>
            </b-input-group>
          </b-col>
          <b-col md="6">
            <b-input-group prepend="Rating">
              <b-form-input
                type="number"
                :readonly="!hasPermission(UserRatingChange)"
                v-model="rankedRating"
              ></b-form-input>
              <b-input-group-append>
                <b-button
                  variant="primary"
                  :disabled="!hasPermission(UserRatingChange) || servers[userData.server].readonly || userLock || !rankedRatingChanged"
                  @click="changeRankedRating"
                >
                  Change
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
      </b-tab>

      <b-tab title="Random">
        <b-row class="mb-3">
          <b-col md="6">
            <b-input-group prepend="Wins">
              <b-form-input readonly :value="userData.pvp.random_wins"></b-form-input>
            </b-input-group>
          </b-col>
          <b-col md="6">
            <b-input-group prepend="Losses">
              <b-form-input readonly :value="userData.pvp.random_losses"></b-form-input>
            </b-input-group>
          </b-col>
        </b-row>

        <b-row class="mb-3">
          <b-col md="6">
            <b-input-group prepend="Current Win-Streak">
              <b-form-input readonly :value="userData.pvp.random_cur_streak"></b-form-input>
              <b-input-group-append is-text v-b-tooltip.hover title="All Time Highest Win-Streak">
                <i class="fa fa-line-chart mr-1"></i> {{ userData.pvp.random_max_streak }}
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col md="6">
            <b-input-group prepend="Wins Since Last PvP Chest">
              <b-form-input readonly :value="userData.pvp.random_chest_wins"></b-form-input>
            </b-input-group>
          </b-col>
        </b-row>

        <b-row class="mb-3">
          <b-col md="6">
            <b-input-group prepend="WOTD" v-b-tooltip.hover title="Win of the Day">
              <b-input-group-prepend is-text>
                <i :class="isWOTD(userData.pvp.random_wotd) ? 'fa fa-check text-success' : 'fa fa-times text-danger'"></i>
              </b-input-group-prepend>
              <b-form-input readonly :value="formatWOTD(userData.pvp.random_wotd)"></b-form-input>
            </b-input-group>
          </b-col>
          <b-col md="6">
            <b-input-group prepend="Rating">
              <b-form-input
                type="number"
                :readonly="!hasPermission(UserRatingChange)"
                v-model="randomRating"
              ></b-form-input>
              <b-input-group-append>
                <b-button
                  variant="primary"
                  :disabled="!hasPermission(UserRatingChange) || servers[userData.server].readonly || userLock || !randomRatingChanged"
                  @click="changeRandomRating"
                >
                  Change
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
      </b-tab>

      <b-tab title="Unranked">
        <b-row>
          <b-col md="6">
            <b-input-group prepend="Wins">
              <b-form-input readonly :value="userData.pvp.unranked_wins"></b-form-input>
            </b-input-group>
          </b-col>
          <b-col md="6">
            <b-input-group prepend="Losses">
              <b-form-input readonly :value="userData.pvp.unranked_losses"></b-form-input>
            </b-input-group>
          </b-col>
        </b-row>
      </b-tab>
    </b-tabs>
  </b-card>
</template>

<script>
import iziToast from 'izitoast'
import VueNotifications from 'vue-notifications'
import moment from 'moment'

export default {
  name: 'PvP',
  props: ['userData', 'userLock', 'servers'],

  data () {
    return {
      rankedRating: this.userData.pvp.ranked_rating,
      randomRating: this.userData.pvp.random_rating
    }
  },

  computed: {
    rankedRatingChanged () {
      return Number(this.rankedRating) !== Number(this.userData.pvp.ranked_rating)
    },
    randomRatingChanged () {
      return Number(this.randomRating) !== Number(this.userData.pvp.random_rating)
    }
  },

  watch: {
    userData (value) {
      this.rankedRating = value.pvp.ranked_rating
      this.randomRating = value.pvp.random_rating
    },
    'userData.pvp.ranked_rating' (value) {
      this.rankedRating = value
    },
    'userData.pvp.random_rating' (value) {
      this.randomRating = value
    }
  },

  methods: {
    isWOTD (date) {
      if (!date) return false
      return moment.utc(date).isSame(moment.utc(), 'day')
    },
    formatWOTD (date) {
      if (!date) return 'Never'
      if (this.isWOTD(date)) return 'Received Today'
      return moment.utc(date).format('YYYY-MM-DD')
    },
    changeRankedRating () {
      if (!this.rankedRatingChanged) {
        return
      }
      let self = this
      iziToast.question({
        timeout: 20000,
        close: true,
        overlay: true,
        toastOnce: true,
        id: 'question',
        zindex: 99999,
        message: 'Are you sure that you want to change <strong>' + this.$route.params.username + '</strong>\'s pvp <strong>ranked</strong> rating to <strong> ' + self.rankedRating + '</strong>?',
        position: 'center',
        buttons: [
          ['<button><b>YES</b></button>', function (instance, toast) {
            iziToast.hide({}, toast)
            self.emit('changeRating', {
              id: self.userData.id,
              value: Number(self.rankedRating),
              server: self.userData.server
            })
          }, true]
        ]
      })
    },
    changeRandomRating () {
      if (!this.randomRatingChanged) {
        return
      }
      let self = this
      iziToast.question({
        timeout: 20000,
        close: true,
        overlay: true,
        toastOnce: true,
        id: 'question',
        zindex: 99999,
        message: 'Are you sure that you want to change <strong>' + this.$route.params.username + '</strong>\'s pvp <strong>random</strong> rating to <strong> ' + self.randomRating + '</strong>?',
        position: 'center',
        buttons: [
          ['<button><b>YES</b></button>', function (instance, toast) {
            iziToast.hide({}, toast)
            self.emit('changeRandomRating', {
              id: self.userData.id,
              value: Number(self.randomRating),
              server: self.userData.server
            })
          }, true]
        ]
      })
    }
  },
  notifications: {
    ChangeSuccessful: {
      type: VueNotifications.types.success,
      title: 'Change successful!',
      message: 'Change was successful!'
    }
  }
}
</script>
